import { useEffect, useState } from 'react';
import { getAdminReports, generateCsvReport, GetAdminReportsOutputType } from 'zite-endpoints-sdk';
import { Skeleton } from '@/components/ui/skeleton';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import { Users, BookOpen, ShieldCheck, Calendar, AlertCircle, UserPlus, Download, Loader2, Filter, ChevronDown, ChevronUp } from 'lucide-react';
import { format, parseISO } from 'date-fns';

type Reports = GetAdminReportsOutputType;
type ReportType = 'employees' | 'onboarding' | 'compliance_gaps' | 'training' | 'certificates' | 'maintenance' | 'coshh' | 'fire_logs' | 'tasks';

const fmtDate = (d?: string) => { try { return d ? format(parseISO(d), 'dd MMM yyyy') : '—'; } catch { return d || '—'; } };

function downloadCsv(csv: string, filename: string) {
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url; a.download = filename; a.click();
  URL.revokeObjectURL(url);
}

const REPORTS: { type: ReportType; label: string; group: string }[] = [
  { type: 'employees', label: 'All Employees', group: 'People' },
  { type: 'onboarding', label: 'Onboarding', group: 'People' },
  { type: 'training', label: 'Training Progress', group: 'Training' },
  { type: 'certificates', label: 'Certificates', group: 'Training' },
  { type: 'compliance_gaps', label: 'Compliance Gaps', group: 'Compliance' },
  { type: 'maintenance', label: 'Maintenance Tasks', group: 'Operations' },
  { type: 'coshh', label: 'COSHH Register', group: 'Operations' },
  { type: 'fire_logs', label: 'Fire Logs', group: 'Operations' },
  { type: 'tasks', label: 'Manager Tasks', group: 'Operations' },
];

export default function ReportsTab() {
  const [data, setData] = useState<Reports | null>(null);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState<string | null>(null);
  const [showFilters, setShowFilters] = useState(false);
  const [filterDept, setFilterDept] = useState('');
  const [filterRole, setFilterRole] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [filterDateFrom, setFilterDateFrom] = useState('');
  const [filterDateTo, setFilterDateTo] = useState('');

  useEffect(() => {
    getAdminReports({})
      .then(setData)
      .catch(() => toast.error('Failed to load reports'))
      .finally(() => setLoading(false));
  }, []);

  const handleDownload = async (type: ReportType, label: string) => {
    setDownloading(type);
    try {
      const filters: Record<string, string> = {};
      if (filterDept) filters.department = filterDept;
      if (filterRole) filters.role = filterRole;
      if (filterStatus) filters.status = filterStatus;
      if (filterDateFrom) filters.dateFrom = filterDateFrom;
      if (filterDateTo) filters.dateTo = filterDateTo;
      const res = await generateCsvReport({ type, filters: Object.keys(filters).length > 0 ? filters : undefined });
      downloadCsv(res.csv, res.filename);
      toast.success(`${label}: ${res.rowCount} rows exported`);
    } catch { toast.error('Failed to generate CSV'); }
    finally { setDownloading(null); }
  };

  if (loading) {
    return (
      <div className="space-y-4">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {[...Array(4)].map((_, i) => <Skeleton key={i} className="h-24" />)}
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {[...Array(4)].map((_, i) => <Skeleton key={i} className="h-40" />)}
        </div>
      </div>
    );
  }

  if (!data) return null;

  const groups = [...new Set(REPORTS.map(r => r.group))];

  return (
    <div className="space-y-6">
      {/* Filter toggle */}
      <div className="flex flex-wrap items-center gap-2">
        <Button variant="outline" size="sm" onClick={() => setShowFilters(p => !p)}>
          <Filter className="w-3.5 h-3.5 mr-1.5" />
          Filters
          {showFilters ? <ChevronUp className="w-3 h-3 ml-1" /> : <ChevronDown className="w-3 h-3 ml-1" />}
        </Button>
        {(filterDept || filterRole || filterStatus || filterDateFrom || filterDateTo) && (
          <Button variant="ghost" size="sm" onClick={() => { setFilterDept(''); setFilterRole(''); setFilterStatus(''); setFilterDateFrom(''); setFilterDateTo(''); }} className="text-xs text-muted-foreground">
            Clear filters
          </Button>
        )}
      </div>

      {showFilters && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3 p-4 bg-muted/30 border border-border rounded-lg">
          <div>
            <label className="text-xs text-muted-foreground mb-1 block">Department</label>
            <Select value={filterDept || '_all'} onValueChange={v => setFilterDept(v === '_all' ? '' : v)}>
              <SelectTrigger className="h-8 text-xs"><SelectValue placeholder="All" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="_all">All</SelectItem>
                {['Front of House', 'Back of House', 'Kitchen', 'Management'].map(d => <SelectItem key={d} value={d}>{d}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-xs text-muted-foreground mb-1 block">Role</label>
            <Select value={filterRole || '_all'} onValueChange={v => setFilterRole(v === '_all' ? '' : v)}>
              <SelectTrigger className="h-8 text-xs"><SelectValue placeholder="All" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="_all">All</SelectItem>
                {['Trainee', 'Staff', 'Supervisor', 'Manager', 'Admin', 'Owner'].map(r => <SelectItem key={r} value={r}>{r}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-xs text-muted-foreground mb-1 block">Status</label>
            <Input className="h-8 text-xs" placeholder="e.g. Open, Signed" value={filterStatus} onChange={e => setFilterStatus(e.target.value)} />
          </div>
          <div>
            <label className="text-xs text-muted-foreground mb-1 block">Date From</label>
            <Input className="h-8 text-xs" type="date" value={filterDateFrom} onChange={e => setFilterDateFrom(e.target.value)} />
          </div>
          <div>
            <label className="text-xs text-muted-foreground mb-1 block">Date To</label>
            <Input className="h-8 text-xs" type="date" value={filterDateTo} onChange={e => setFilterDateTo(e.target.value)} />
          </div>
        </div>
      )}

      {/* CSV Exports grouped */}
      <div className="space-y-3">
        {groups.map(group => (
          <div key={group}>
            <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2">{group}</p>
            <div className="flex flex-wrap gap-2">
              {REPORTS.filter(r => r.group === group).map(r => (
                <Button key={r.type} variant="outline" size="sm" onClick={() => handleDownload(r.type, r.label)} disabled={!!downloading}>
                  {downloading === r.type ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <Download className="w-3.5 h-3.5 mr-1.5" />}
                  {r.label} CSV
                </Button>
              ))}
            </div>
          </div>
        ))}
      </div>

      {/* Summary cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="border border-border bg-card rounded-lg p-4">
          <div className="flex items-center gap-2 mb-1">
            <Users className="w-4 h-4 text-primary" />
            <p className="text-xs text-muted-foreground uppercase tracking-wider">Total Staff</p>
          </div>
          <p className="text-2xl font-bold text-foreground">{data.headcount.total}</p>
          <p className="text-xs text-muted-foreground mt-1">
            {data.headcount.byEmploymentType.map(t => t.count + ' ' + t.type.replace('Full Time', 'FT').replace('Part Time', 'PT')).join(' · ')}
          </p>
        </div>
        <div className="border border-border bg-card rounded-lg p-4">
          <div className="flex items-center gap-2 mb-1">
            <UserPlus className="w-4 h-4 text-primary" />
            <p className="text-xs text-muted-foreground uppercase tracking-wider">Onboarding</p>
          </div>
          <p className="text-2xl font-bold text-foreground">{data.onboarding.inProgress + data.onboarding.submitted}</p>
          <p className="text-xs text-muted-foreground mt-1">In progress or awaiting approval</p>
        </div>
        <div className="border border-border bg-card rounded-lg p-4">
          <div className="flex items-center gap-2 mb-1">
            <BookOpen className="w-4 h-4 text-primary" />
            <p className="text-xs text-muted-foreground uppercase tracking-wider">Training</p>
          </div>
          <p className="text-2xl font-bold text-foreground">{data.training.totalCompletions}</p>
          <p className="text-xs text-muted-foreground mt-1">{data.training.uniqueUsers} staff with completions</p>
        </div>
        <div className="border border-border bg-card rounded-lg p-4">
          <div className="flex items-center gap-2 mb-1">
            <ShieldCheck className="w-4 h-4 text-primary" />
            <p className="text-xs text-muted-foreground uppercase tracking-wider">Compliance</p>
          </div>
          <p className={`text-2xl font-bold ${data.compliance.overdue > 0 ? 'text-destructive' : 'text-foreground'}`}>
            {data.compliance.overdue + data.compliance.dueSoon}
          </p>
          <p className="text-xs text-muted-foreground mt-1">
            {data.compliance.overdue > 0 ? `${data.compliance.overdue} overdue · ` : ''}{data.compliance.dueSoon} due this week
          </p>
        </div>
      </div>

      {/* Action alerts */}
      {(data.pendingTrainingRequests > 0 || data.pendingEditRequests > 0 || data.compliance.overdue > 0) && (
        <div className="border border-amber-500/30 bg-amber-500/5 rounded-lg p-4 space-y-2">
          <p className="text-sm font-medium text-foreground flex items-center gap-2">
            <AlertCircle className="w-4 h-4 text-amber-500" /> Action Required
          </p>
          <div className="space-y-1 text-xs text-muted-foreground">
            {data.pendingTrainingRequests > 0 && <p>• {data.pendingTrainingRequests} pending training request(s)</p>}
            {data.pendingEditRequests > 0 && <p>• {data.pendingEditRequests} pending profile change request(s)</p>}
            {data.compliance.overdue > 0 && <p className="text-destructive">• {data.compliance.overdue} overdue compliance form(s)</p>}
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Headcount by Department */}
        <div className="border border-border bg-card rounded-lg p-4">
          <p className="text-sm font-semibold text-foreground mb-3 flex items-center gap-2">
            <Users className="w-4 h-4 text-primary" /> Headcount by Department
          </p>
          <div className="space-y-2.5">
            {data.headcount.byDepartment.map(dept => (
              <div key={dept.department} className="flex items-center gap-3">
                <span className="text-sm text-muted-foreground w-36 truncate">{dept.department}</span>
                <div className="flex-1 bg-muted rounded-full h-1.5 overflow-hidden">
                  <div className="bg-primary h-full rounded-full" style={{ width: `${(dept.count / data.headcount.total) * 100}%` }} />
                </div>
                <span className="text-sm font-medium text-foreground w-6 text-right">{dept.count}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Onboarding Funnel */}
        <div className="border border-border bg-card rounded-lg p-4">
          <p className="text-sm font-semibold text-foreground mb-3">Onboarding Funnel</p>
          <div className="space-y-2">
            {[
              { label: 'Approved', count: data.onboarding.approved, color: 'bg-primary' },
              { label: 'Submitted', count: data.onboarding.submitted, color: 'bg-amber-500' },
              { label: 'In Progress', count: data.onboarding.inProgress, color: 'bg-muted-foreground' },
              { label: 'Not Started', count: data.onboarding.notStarted, color: 'bg-border' },
            ].map(item => (
              <div key={item.label} className="flex justify-between items-center text-sm">
                <div className="flex items-center gap-2">
                  <div className={`w-2 h-2 rounded-full ${item.color}`} />
                  <span className="text-muted-foreground">{item.label}</span>
                </div>
                <span className="font-medium text-foreground">{item.count}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Recent Starters */}
        <div className="border border-border bg-card rounded-lg p-4">
          <p className="text-sm font-semibold text-foreground mb-3 flex items-center gap-2">
            <UserPlus className="w-4 h-4 text-primary" /> Recent Starters (30 days)
          </p>
          {data.recentStarters.length === 0 ? (
            <p className="text-xs text-muted-foreground">No new starters in the last 30 days</p>
          ) : (
            <div className="space-y-2">
              {data.recentStarters.map(s => (
                <div key={s.id} className="flex justify-between text-sm">
                  <div>
                    <p className="text-foreground font-medium">{s.name}</p>
                    {s.department && <p className="text-xs text-muted-foreground">{s.department}</p>}
                  </div>
                  <span className="text-xs text-muted-foreground">{fmtDate(s.startDate)}</span>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Upcoming Reviews */}
        <div className="border border-border bg-card rounded-lg p-4">
          <p className="text-sm font-semibold text-foreground mb-3 flex items-center gap-2">
            <Calendar className="w-4 h-4 text-primary" /> Upcoming Reviews
          </p>
          {data.upcomingReviews.length === 0 ? (
            <p className="text-xs text-muted-foreground">No upcoming reviews scheduled</p>
          ) : (
            <div className="space-y-2">
              {data.upcomingReviews.map(r => (
                <div key={r.id} className="flex justify-between text-sm">
                  <div>
                    <p className="text-foreground font-medium">{r.name}</p>
                    {r.department && <p className="text-xs text-muted-foreground">{r.department}</p>}
                  </div>
                  <span className="text-xs text-muted-foreground">{fmtDate(r.nextReviewDate)}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Redaction notice */}
      <div className="text-xs text-muted-foreground bg-muted/30 border border-border rounded-lg p-3">
        <strong>Data protection:</strong> CSV exports exclude sensitive HR fields (NI number, bank details, wages, date of birth, addresses). Admin/Owner exports include email addresses; Manager exports do not.
      </div>
    </div>
  );
}
